import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetPolicyOptions,
  GetPolicyResponse,
  Policy,
  PolicyCreatedEnvelope,
  PolicyUpdatedEnvelope,
  UpdatePolicy,
  UpdatePolicyResponse,
  createPolicy as universalCreatePolicy,
  getPolicy as universalGetPolicy,
  updatePolicy as universalUpdatePolicy,
} from './restaurants-order-pacing-v1-policy-order-pacing-policy.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/restaurants' };

export function createPolicy(httpClient: HttpClient): CreatePolicySignature {
  return (policy: NonNullablePaths<Policy, `rules`, 2>) =>
    universalCreatePolicy(
      policy,
      // @ts-ignore
      { httpClient }
    );
}

interface CreatePolicySignature {
  /**
   * Creates a new Policy
   * The default location id for the metaSite will be used if the location_id is not provided
   * @param - The policy to be created
   * @returns The created Policy
   */
  (policy: NonNullablePaths<Policy, `rules`, 2>): Promise<
    NonNullablePaths<
      Policy,
      | `enabled`
      | `rules`
      | `rules.${number}.volumeLimit`
      | `rules.${number}.includeContactlessDineinOrders`,
      4
    >
  >;
}

export function getPolicy(httpClient: HttpClient): GetPolicySignature {
  return (options?: GetPolicyOptions) =>
    universalGetPolicy(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetPolicySignature {
  /**
   * Get a Policy by location_id
   * The default location id for the metaSite will be used if the location_id is not provided
   */
  (options?: GetPolicyOptions): Promise<
    NonNullablePaths<
      GetPolicyResponse,
      | `policy.enabled`
      | `policy.rules`
      | `policy.rules.${number}.volumeLimit`
      | `policy.rules.${number}.includeContactlessDineinOrders`,
      5
    >
  >;
}

export function updatePolicy(httpClient: HttpClient): UpdatePolicySignature {
  return (
    _id: string,
    policy: NonNullablePaths<UpdatePolicy, `revision` | `rules`, 2>
  ) =>
    universalUpdatePolicy(
      _id,
      policy,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdatePolicySignature {
  /**
   * Update a Policy
   * The default location id for the metaSite will be used if the location_id is not provided
   * Pass the latest `revision` for a successful update
   * @param - The policy's id
   */
  (
    _id: string,
    policy: NonNullablePaths<UpdatePolicy, `revision` | `rules`, 2>
  ): Promise<
    NonNullablePaths<
      UpdatePolicyResponse,
      | `policy.enabled`
      | `policy.rules`
      | `policy.rules.${number}.volumeLimit`
      | `policy.rules.${number}.includeContactlessDineinOrders`,
      5
    >
  >;
}

export const onPolicyCreated = EventDefinition(
  'wix.restaurants.order_pacing.v1.policy_created',
  true,
  (event: PolicyCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<PolicyCreatedEnvelope>();
export const onPolicyUpdated = EventDefinition(
  'wix.restaurants.order_pacing.v1.policy_updated',
  true,
  (event: PolicyUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<PolicyUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  BaseEventMetadata,
  CreatePolicyRequest,
  CreatePolicyResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetPolicyOptions,
  GetPolicyRequest,
  GetPolicyResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  MessageEnvelope,
  Policy,
  PolicyCreatedEnvelope,
  PolicyRule,
  PolicyUpdatedEnvelope,
  RestaurantLocationProperties,
  RestaurantLocationsUpdatedEvent,
  RestoreInfo,
  SpecialEvent,
  SyncLocationsSource,
  Type,
  UpdatePolicy,
  UpdatePolicyRequest,
  UpdatePolicyResponse,
  WebhookIdentityType,
  WeeklyTimeInterval,
} from './restaurants-order-pacing-v1-policy-order-pacing-policy.universal.js';
